import 'regenerator-runtime/runtime';
import { call, delay, put, select, take, takeEvery } from 'redux-saga/effects';
import {
  AngebotsId,
  DoRequestResponse,
  getAngebotsIds,
  getEndpointUrl,
  GlobalActions,
  GlobalEventDispatcher,
  GlobalPut,
  SavedState,
  WrappedSavedStates
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  AvErrorMessageEnum,
  getInactiveProductApps,
  getProtectedAngebotsIds,
  isEntryProtectedOrActive,
} from '@ps20-frontend/frontend-models';
import { AxiosError } from 'axios';
import { AVLadeVorgangResponse } from '@ps20/bff-models';
import { PS20Logger } from '@ps20-frontend/logging';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { isUwAzam } from '@ps20-frontend/utils';
import { IAppState } from '../localStore';
import { shouldUseSdwEndpoint } from '../selectors/transientState.selector';
import { SaveProtocol } from '../saveProtocol.reducer';
import { LZString } from './helpers/stringCompressor';

const PLACEHOLDER = 'no_sensitive_data';
const globalActions = new GlobalActions();

export function* watchLoadActionRequested() {
  yield takeEvery(GlobalActions.EINSTIEG_REQUEST_SUCCESSFUL, safeSaga(watchLoadAvSaga, App.SHOP));
  yield takeEvery(GlobalActions.LOAD_AV_REQUEST_SUCCESSFUL, safeSaga(successCallback, App.SHOP));
  yield takeEvery(GlobalActions.LOAD_AV_REQUEST_FAILED, safeSaga(errorCallback, App.SHOP));
}

export function* watchLoadAvSaga() {
  const state: IAppState = yield select();

  const cryptId = state.transientState.datenhaltung.av.cryptId;
  if (cryptId) {
    const body = {
      cryptId
    };

    yield put(
      globalActions.doRequest({
        appId: App.SHOP,
        url: getEndpointUrl(shouldUseSdwEndpoint(state), '/privatschutz/api/load'),
        body,
        successEvent: GlobalActions.LOAD_AV_REQUEST_SUCCESSFUL,
        errorEvent: GlobalActions.LOAD_AV_REQUEST_FAILED
      })
    );
  }
}

export function loadSavedAv(storedData: string, sensitiveStores?: boolean): WrappedSavedStates {
  const decompressedStores = LZString.decompressFromBase64(storedData);
  if (!decompressedStores) {
    throw new Error('Error occurred while loading saved AV data!');
  }
  const parsedState = JSON.parse(decompressedStores);
  if (sensitiveStores || Array.isArray(parsedState)) {
    return parsedState;
  }
  const legacyState: { [key: string]: SavedState } = parsedState;

  return Object.keys(legacyState).map(appId => ({
    appId: appId as App,
    angebotsId: legacyState[appId].angebotsId,
    state: legacyState[appId]
  }));
}

export function* successCallback(action: DoRequestResponse<AVLadeVorgangResponse>) {
  if (action.payload.capFrontendUrl) {
    yield call(navigateToUrl, action.payload.capFrontendUrl);
    return;
  }
  yield take(GlobalActions.ALL_MISSING_STORES_CREATED);
  const shopState: IAppState = yield select();
  const featureToggles = shopState.transientState.featureToggles;
  const savedStates = filterOutInactiveProductVersions(loadSavedAv(action.payload.stores), featureToggles);
  let savedSensitiveStates = null;
  if (action.payload.sensitiveStores && action.payload.sensitiveStores !== PLACEHOLDER) {
    savedSensitiveStates = filterOutInactiveProductVersions(
      loadSavedAv(action.payload.sensitiveStores, true),
      featureToggles,
    );
  }
  if (action.payload.sensitiveStores && action.payload.sensitiveStores === PLACEHOLDER) {
    savedSensitiveStates = PLACEHOLDER;
  }
  const globalEventDispatcher = GlobalEventDispatcher.getInstance();
  globalEventDispatcher.dispatch(
    globalActions.savedStatesLoaded(savedStates, savedSensitiveStates as unknown as SavedState)
  );
  globalEventDispatcher.dispatch(
    globalActions.savedDokumentIdReceived(action.payload.vorschlagDokumentId)
  );
  if (action.payload.underReview === true) {
    const currentCryptId = shopState.transientState.datenhaltung?.av?.cryptId;
    if (currentCryptId) {
      globalEventDispatcher.dispatch(
        globalActions.saveStatusReceived({
          cryptId: currentCryptId,
          status: 200,
          underReview: action.payload.underReview,
          angebotsBuendelId: action.payload.angebotsBuendelId,
        }),
      );
    }
  }
  if (
    isUwAzam(
      shopState.transientState.datenhaltung.kdv.perId,
      shopState.transientState.datenhaltung.sdw?.role
    )
  ) {
    globalEventDispatcher.dispatch(
      new GlobalActions().roleDetermined(
        savedStates.find(s => s.appId === App.SHOP_CHECKOUT)?.state?.role
      )
    );
  }
  const loadedShopState = savedStates.find(loadedState => loadedState.appId === App.SHOP);
  if (loadedShopState?.state.saveProtocol) {
    const saveProtocol: SaveProtocol = loadedShopState.state.saveProtocol as SaveProtocol;
    const bffVersion = saveProtocol.bffVersion;
    const correlationId = saveProtocol.correlationId;
    const msg = '{bffVersion: ' + `${bffVersion}` + ', correlationId: ' + `${correlationId}` + '}';
    PS20Logger.info('saveProtocol', msg, App.SHOP);
  }

  if (isVorgangCopied(shopState)) {
    const angebotsIdForAvailableStores: Array<AngebotsId> = yield call(getAngebotsIds);

    angebotsIdForAvailableStores.forEach(angebotsId => {
      globalEventDispatcher.dispatch(
        globalActions.antragsnummerRequested(angebotsId.appId, angebotsId.angebotsId)
      );
    });
  }
  delay(0);
  globalEventDispatcher.dispatch(globalActions.updateLoadedStates());
}

function isVorgangCopied(shopState: IAppState): boolean {
  return shopState.transientState.datenhaltung.kdv.copyFlag === 1;
}

export function isStreckenApp(appId: App): boolean {
  return ![App.SHOP_DASHBOARD, App.SHOP_TOOLBAR, App.SHOP_CHECKOUT, App.SHOP].includes(appId);
}

export function filterOutInactiveProductVersions(
  states: WrappedSavedStates,
  featureToggles: { [key: string]: boolean },
): WrappedSavedStates {
  if (!Array.isArray(states)) {
    return states;
  }

  const inactiveApps = getInactiveProductApps(featureToggles);
  const protectedAngebotsIds = getProtectedAngebotsIds(states, App.SHOP_CHECKOUT);
  return states.filter(state =>
    isEntryProtectedOrActive(state, inactiveApps, protectedAngebotsIds),
  );
}


/**
 * @deprecated Use filterOutInactiveProductVersions instead
 */
export function filterOutInactiveTkvVersion(
  states: WrappedSavedStates,
): WrappedSavedStates {
  if (!Array.isArray(states)) {
    return states;
  }
  return states.filter(state => state.appId !== App.TKV);
}

export function* errorCallback(errorAction: DoRequestResponse<AxiosError>) {
  let errorMessage = AvErrorMessageEnum.LOAD_AV_ERROR;
  if (errorAction.payload.response.status === 400) {
    errorMessage = AvErrorMessageEnum.CRYPT_ID_ERROR;
  }
  if (errorAction.payload.response.status === 500) {
    errorMessage = AvErrorMessageEnum.EINREICHEN_ERROR;
  }
  const globalPut = GlobalPut.getInstance();
  yield call(globalPut.put, globalActions.errorPageWithCustomContentRequested(errorMessage));
}

/* istanbul ignore next */
export const navigateToUrl = (url: string) => {
  window.location.href = url;
};
